import React from 'react';
import {Box, CircularProgress, Typography} from "@mui/joy";
import {useQuery} from "@apollo/client";
import {useTranslation} from "react-i18next";
import _ from "lodash";
import {ActionsMenu} from "@dreebit-registry/vsm-microservice-components";
import {WidgetActionsMenu} from "../WidgetActionsMenu";
import {ClientServiceUnit} from "../../graphql/queries/clientServiceUnit";
import useOwnClientId from "../../hooks/useOwnClientId";
import ServiceUnitContent from "../ServiceUnitPanel/ServiceUnitContent";
import {getServiceUnitSourceInfo} from "../../utils/serviceUnitSource";

interface Props {
    onDelete: () => void;
    onEdit: () => void;
}

/**
 * Dashboard widget answering "who serves my company?" from the resolved chain
 * on the user's own client (IBM-972). The title and chip change with the
 * resolution source so a region/global fallback never reads as a dedicated
 * assignment.
 */
const ServiceUnitWidget = ({onDelete, onEdit}: Props) => {
    const {t} = useTranslation();
    const clientId = useOwnClientId();
    const actionItems = WidgetActionsMenu(onDelete, onEdit);

    const query = useQuery(ClientServiceUnit, {
        variables: {clientId},
        skip: !clientId,
    });
    const resolved = _.get(query, 'data.client.serviceUnit', null);
    const sourceInfo = getServiceUnitSourceInfo(resolved?.source, t, {unitType: resolved?.unit?.type});
    const title = resolved && !sourceInfo.isFallback ? sourceInfo.title : t('Central service contact');

    return (
        <Box
            data-testid="service-unit-widget"
            sx={{display: 'flex', flexDirection: 'column', width: '100%', height: '100%'}}
        >
            <Box sx={{display: 'flex', alignItems: 'center'}}>
                <Typography level="titleSmallLight" sx={{flex: 1, textAlign: 'left', paddingLeft: '10px', color: '#333'}}>
                    {title}
                </Typography>
                <div className="no-drag">
                    <ActionsMenu items={actionItems}/>
                </div>
            </Box>
            {/* No inner scrolling — the widget is tall enough for the unit block
                plus two named contacts; anything beyond is summarised. */}
            <Box className="no-drag" sx={{flex: 1, minHeight: 0, overflow: 'hidden', px: '10px', pb: '6px'}}>
                {query.loading && !resolved ? (
                    <Box sx={{height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <CircularProgress variant="soft" size="sm"/>
                    </Box>
                ) : !resolved?.unit ? (
                    <Typography level="body-sm" sx={{color: 'text.secondary', pt: '12px'}}>
                        {t('No service contact is configured yet.')}
                    </Typography>
                ) : (
                    <Box sx={{pt: '6px'}}>
                        <ServiceUnitContent resolved={resolved} maxContacts={2}/>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default ServiceUnitWidget;
